import { useCallback, useEffect, useId } from 'react';
import { noop } from '@/utils/function-utils';
import type {
  IExternalScriptLoaderProps,
  ScriptLoaderMethod
} from '../IScriptLoader';

/**
 * Properties for the `useExternalScript` hook.
 *
 * Extends the base properties for loading an external script, adding the `method` property
 * to specify the loading method.
 */
interface IUseExternalScriptProps
  extends Omit<IExternalScriptLoaderProps, 'method'> {
  /**
   * The method to use for loading the script.
   *
   * We support all methods in the hook, to be able to check which ones
   * to execute the script loading logic.
   */
  method: ScriptLoaderMethod;
}

/**
 * Internal hook from `<ScriptLoader />` component, to load a script into the document
 * **without** using Next.js `<Script>`.
 *
 * @param props - The properties of the external script. Usually an interface for what HTMLScriptElement can take.
 */
export const useExternalScript = ({
  id,
  type = 'text/javascript',
  src,
  async = true,
  shouldRemoveOnUnmount = true,
  method,
  onError,
  onLoad
}: IUseExternalScriptProps): void => {
  const internalScriptId = useId();

  // #region Callback handlers for script events
  const externalScriptOnError = useCallback(
    (event: string | Event) => {
      // The present message in an external document error event may be a string or an object.
      // We need to handle both cases to create a proper error message.
      const message =
        typeof event === 'string'
          ? // If the event is a string, use it directly.
            event
          : // If is an event object and have a string message, use it.
            'message' in event && typeof event.message === 'string'
            ? event.message
            : 'An error occurred while loading the script.';

      const error = new Error(`Error loading script ${src}: ${message}`);
      onError?.(error);
    },
    [onError, src]
  );
  // #endregion

  /**
   * Generates a script element, or returns the existing one if it was already created.
   * This is to avoid creating multiple script elements with the same ID, specially
   * when `shouldRemoveOnUnmount` is false.
   *
   * @returns The script element.
   */
  const generateScriptElement = useCallback((): HTMLScriptElement => {
    const existingScript = document.getElementById(id ?? internalScriptId);

    if (existingScript instanceof HTMLScriptElement) {
      return existingScript;
    }

    const script = document.createElement('script');

    script.id = id ?? internalScriptId;
    return script;
  }, [id, internalScriptId]);

  /**
   * Effect to create the script element and append it to the document body.
   * Also handles the cleanup of the script element if `shouldRemoveOnUnmount` is true.
   *
   * We intentionally do not append the script to the document head, as some scripts.
   * Like analytics, may not work properly when loaded in the head.
   */
  useEffect(() => {
    // Only executes injection if the method is 'external'.
    if (method === 'external') {
      const script = generateScriptElement();

      script.src = src ?? '';
      script.async = async;
      script.type = type ?? 'text/javascript';
      script.onload = onLoad ?? null;
      script.onerror = externalScriptOnError;

      document.body.appendChild(script);

      return () => {
        if (shouldRemoveOnUnmount) {
          script.remove();
        }
      };
    }

    // Cleanup with no operation function in case of using Next.js Script component.
    return noop;
  }, [
    async,
    externalScriptOnError,
    generateScriptElement,
    shouldRemoveOnUnmount,
    src,
    method,
    onLoad,
    type
  ]);
};
